import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  RadixSelect,
  type SelectOption,
} from '@/components/ui';
import { useUserGradesQuery } from '../../grades/hooks/useUserGradesQuery';
import { useUserDetailQuery } from '../../hooks/useUserDetailQuery';
import { BusinessDocumentPreview } from './BusinessDocumentPreview';

export interface BusinessApproveModalProps {
  open: boolean;
  memberName: string;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onConfirm: (gradeId: string | null) => void;
  onCancel: () => void;
  /** D4 — 승인 대상 회원 ID. documentUrl 미제공 시 상세를 조회해 서류 URL 을 보강한다. */
  userId?: string | undefined;
  /** D4 — 사업자등록증 파일 URL. 목록 응답에 포함되면 즉시 사용(상세 조회 생략). */
  documentUrl?: string | null | undefined;
}

const NO_GRADE = '';
const GRADE_FETCH_SIZE = 100;

/**
 * R6 O — 사업자회원 승인 모달. 승인 시 부여할 등급을 선택(선택 안 함 = 등급 미부여)한다.
 * 등급 목록은 기존 user-grades 조회를 재사용한다. 계약: ID-O-be.md POST .../business/approve.
 */
export const BusinessApproveModal = ({
  open,
  memberName,
  isSubmitting,
  errorMessage,
  onConfirm,
  onCancel,
  userId,
  documentUrl,
}: BusinessApproveModalProps) => {
  const { t } = useTranslation('common');
  const gradesQuery = useUserGradesQuery({ page: 1, size: GRADE_FETCH_SIZE });
  const [gradeId, setGradeId] = useState<string>(NO_GRADE);

  // 목록에 서류 URL 이 없을 때만 상세를 조회해 보강(모달 열림 + userId + URL 미제공 조건).
  const needsDetail = open && !documentUrl && Boolean(userId);
  const detailQuery = useUserDetailQuery(needsDetail ? (userId ?? '') : '');
  const resolvedDocumentUrl = documentUrl ?? detailQuery.data?.businessDocumentUrl ?? null;

  useEffect(() => {
    if (open) setGradeId(NO_GRADE);
  }, [open]);

  const gradeOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: NO_GRADE, label: t('businessMembers.approve.noGrade', '등급 미부여') },
      ...(gradesQuery.data?.content ?? [])
        .filter((g) => g.active)
        .map((g) => ({ value: g.gradeId, label: g.gradeName })),
    ],
    [gradesQuery.data, t],
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="business-approve-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('businessMembers.approve.title', '사업자회원 승인')}</DialogTitle>
          <DialogDescription>
            {t('businessMembers.approve.subject', { memberName })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <p className="text-sm" style={{ color: 'var(--content-muted)' }}>
            {t('businessMembers.document.label', '사업자등록증')}
          </p>
          <BusinessDocumentPreview
            documentUrl={resolvedDocumentUrl}
            loading={needsDetail && detailQuery.isPending}
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="business-approve-grade"
            className="text-sm"
            style={{ color: 'var(--content-muted)' }}
          >
            {t('businessMembers.approve.gradeLabel', '부여할 등급')}
          </label>
          <RadixSelect
            value={gradeId}
            onChange={setGradeId}
            options={gradeOptions}
            disabled={gradesQuery.isPending || isSubmitting}
            aria-label={t('businessMembers.approve.gradeLabel', '부여할 등급')}
          />
        </div>

        {errorMessage ? (
          <p
            role="alert"
            aria-live="polite"
            className="rounded-md p-2 text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            {errorMessage}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
            {t('businessMembers.cancel', '취소')}
          </Button>
          <Button
            type="button"
            variant="cta"
            size="sm"
            disabled={isSubmitting}
            onClick={() => onConfirm(gradeId === NO_GRADE ? null : gradeId)}
          >
            {isSubmitting
              ? t('businessMembers.submitting', '처리 중...')
              : t('businessMembers.approve.confirm', '승인')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
